// packages/utils/src/prism-scoring.ts
// FRAME — PRISM scoring logic: preference learning, alignment, score modification
//
// All functions are PURE — no side effects, no storage access.
// Storage is handled by web/src/services/prismStore.ts (web) or native modules (mobile).

import type {
  DimensionScores,
  PrismAestheticProperties,
  PrismPreferenceVector,
  PrismOwnerBaseline,
  PrismScoreModifier,
  PrismBehavioralEvent,
} from '@frame/scoring-types';

import {
  DEFAULT_PREFERENCE_VECTOR,
  DEFAULT_OWNER_BASELINE,
  BEHAVIORAL_SIGNAL_WEIGHTS,
  PRISM_CONFIDENCE_RAMP as CONFIDENCE_RAMP,
} from '@frame/scoring-types';

import { clamp } from './format';

/** Safe number: returns fallback if value is NaN/undefined/null. Prevents NaN propagation. */
function safe(value: number | null | undefined, fallback: number = 0): number {
  return (value !== null && value !== undefined && !Number.isNaN(value)) ? value : fallback;
}

// ─── Aesthetic Property Extraction ─────────────────────────────
// Maps already-computed signals from the scoring pipeline to PRISM's schema.
// Most properties are intermediate values that previously weren't captured.

export interface ScoringIntermediates {
  // From singlePassAnalysis
  avgSat: number;
  // From scoreLighting
  warmthRatio: number;     // R/B ratio (1.0 = neutral, >1.0 = warm)
  contrastStdDev: number;  // Luminance std dev
  // From scoreComposition
  thirdsScore: number;     // Rule-of-thirds alignment 0-1
  symmetryScore: number;   // Left-right visual weight balance 0-1
  bgComplexity: number;    // Background edge density 0-1
  // From scoreFaces / analyzeExpression
  smileIntensity: number;  // Average mouth smile blendshape 0-1
  eyeContact: number;      // Gaze consistency 0-1
  naturalness: number;     // Expression naturalness 0-1
  largestFaceRatio: number;
  faceCount: number;
  isSelfie: boolean;
  // Dimension scores (already calibrated)
  portraitScore: number | null;
  lightingScore: number;
  compositionScore: number;
  sceneScore: number;
  compositeScore: number;
}

export function extractAestheticProperties(
  intermediates: ScoringIntermediates,
): PrismAestheticProperties {
  return {
    portraitScore: intermediates.portraitScore,
    lightingScore: intermediates.lightingScore,
    compositionScore: intermediates.compositionScore,
    sceneScore: intermediates.sceneScore,
    compositeScore: intermediates.compositeScore,

    // Map R/B ratio to 0-1 warmth (0.8=cool → 0.3, 1.0=neutral → 0.5, 1.6=warm → 0.9)
    colorWarmth: clamp((safe(intermediates.warmthRatio, 1.0) - 0.6) / 1.2, 0, 1),
    colorSaturation: clamp(safe(intermediates.avgSat, 0.5), 0, 1),
    // Map stdDev to 0-1 contrast (20=low → 0.25, 50=medium → 0.63, 80=high → 1.0)
    contrastLevel: clamp(safe(intermediates.contrastStdDev, 40) / 80, 0, 1),
    cropTightness: clamp(safe(intermediates.largestFaceRatio), 0, 1),
    backgroundComplexity: clamp(safe(intermediates.bgComplexity, 0.5), 0, 1),
    ruleOfThirdsScore: clamp(safe(intermediates.thirdsScore, 0.3), 0, 1),
    symmetryScore: clamp(safe(intermediates.symmetryScore, 0.5), 0, 1),

    smileIntensity: clamp(safe(intermediates.smileIntensity), 0, 1),
    eyeContactScore: clamp(safe(intermediates.eyeContact, 0.5), 0, 1),
    expressionNaturalness: clamp(safe(intermediates.naturalness, 0.5), 0, 1),

    isOwnerPresent: false, // Set by owner detection system
    faceCount: intermediates.faceCount,
    isSelfie: intermediates.isSelfie,
  };
}

// ─── Preference Vector Learning ────────────────────────────────
// Gradient-based learning: compare aesthetic centroids of positive vs negative photos.

interface AestheticCentroid {
  warmth: number;
  saturation: number;
  contrast: number;
  crop: number;
  bgComplexity: number;
  thirds: number;
  symmetry: number;
  smile: number;
  eyeContact: number;
  naturalness: number;
  portrait: number;
  lighting: number;
  composition: number;
  scene: number;
}

function computeCentroid(photos: PrismAestheticProperties[]): AestheticCentroid {
  if (photos.length === 0) {
    return {
      warmth: 0.5, saturation: 0.5, contrast: 0.5, crop: 0.5, bgComplexity: 0.5,
      thirds: 0.5, symmetry: 0.5, smile: 0.5, eyeContact: 0.5, naturalness: 0.5,
      portrait: 5, lighting: 5, composition: 5, scene: 5,
    };
  }

  const n = photos.length;
  const sum = (fn: (p: PrismAestheticProperties) => number) =>
    photos.reduce((s, p) => s + fn(p), 0) / n;

  return {
    warmth: sum(p => p.colorWarmth),
    saturation: sum(p => p.colorSaturation),
    contrast: sum(p => p.contrastLevel),
    crop: sum(p => p.cropTightness),
    bgComplexity: sum(p => p.backgroundComplexity),
    thirds: sum(p => p.ruleOfThirdsScore),
    symmetry: sum(p => p.symmetryScore),
    smile: sum(p => p.smileIntensity),
    eyeContact: sum(p => p.eyeContactScore),
    naturalness: sum(p => p.expressionNaturalness),
    portrait: sum(p => p.portraitScore ?? 5),
    lighting: sum(p => p.lightingScore),
    composition: sum(p => p.compositionScore),
    scene: sum(p => p.sceneScore),
  };
}

/**
 * Update the preference vector based on behavioral signals.
 * Computes a gradient from negative → positive aesthetic centroids and nudges the vector.
 */
export function updatePreferenceVector(
  current: PrismPreferenceVector,
  positivePhotos: PrismAestheticProperties[],
  negativePhotos: PrismAestheticProperties[],
  learningRate: number = 0.1,
): PrismPreferenceVector {
  if (positivePhotos.length === 0 && negativePhotos.length === 0) return current;

  const posCentroid = computeCentroid(positivePhotos);
  const negCentroid = computeCentroid(negativePhotos);

  // Gradient: direction from negative → positive (what user prefers MORE of)
  // Style values are 0-1, dimension values are 0-10. Use separate gradient functions.
  const styleGrad = (prev: number, posVal: number, negVal: number): number => {
    const gradient = posVal - negVal; // Already 0-1 scale
    return clamp(prev + gradient * learningRate, -1, 1);
  };
  const dimGrad = (prev: number, posVal: number, negVal: number): number => {
    const gradient = (posVal - negVal) / 10; // Normalize 0-10 → 0-1
    return clamp(prev + gradient * learningRate, -1, 1);
  };

  const sampleCount = positivePhotos.length + negativePhotos.length;
  const newConfidence = Math.min(1.0,
    current.confidence + sampleCount * 0.01,
  );

  return {
    // Dimensional affinities (0-10 scale centroids)
    portraitAffinity: dimGrad(current.portraitAffinity, posCentroid.portrait, negCentroid.portrait),
    lightingAffinity: dimGrad(current.lightingAffinity, posCentroid.lighting, negCentroid.lighting),
    compositionAffinity: dimGrad(current.compositionAffinity, posCentroid.composition, negCentroid.composition),
    sceneAffinity: dimGrad(current.sceneAffinity, posCentroid.scene, negCentroid.scene),
    // Style preferences (0-1 scale centroids)
    warmthPreference: styleGrad(current.warmthPreference, posCentroid.warmth, negCentroid.warmth),
    contrastPreference: styleGrad(current.contrastPreference, posCentroid.contrast, negCentroid.contrast),
    saturationPreference: styleGrad(current.saturationPreference, posCentroid.saturation, negCentroid.saturation),
    cropPreference: styleGrad(current.cropPreference, posCentroid.crop, negCentroid.crop),
    backgroundPreference: styleGrad(current.backgroundPreference, 1 - posCentroid.bgComplexity, 1 - negCentroid.bgComplexity),
    // Expression preferences (0-1 scale centroids)
    smilePreference: styleGrad(current.smilePreference, posCentroid.smile, negCentroid.smile),
    eyeContactPreference: styleGrad(current.eyeContactPreference, posCentroid.eyeContact, negCentroid.eyeContact),
    poseNaturalness: styleGrad(current.poseNaturalness, posCentroid.naturalness, negCentroid.naturalness),
    // Meta
    confidence: newConfidence,
    photosSampled: current.photosSampled + sampleCount,
    lastUpdated: new Date().toISOString(),
  };
}

// ─── Preference Alignment ──────────────────────────────────────
// Weighted dot product: how well a photo matches the user's learned preferences.

/**
 * Compute how well a photo's aesthetic properties align with user preferences.
 * Returns -1 (completely opposite to user taste) to +1 (perfect match).
 */
export function computePreferenceAlignment(
  photo: PrismAestheticProperties,
  pref: PrismPreferenceVector,
): number {
  // Map photo values (0-1) to centered (-1 to +1) for dot product
  const center = (v: number) => v * 2 - 1;

  const signals: Array<[number, number, number]> = [
    // [photoValue (centered), preferenceValue, weight]
    // Style
    [center(photo.colorWarmth), pref.warmthPreference, 1.0],
    [center(photo.contrastLevel), pref.contrastPreference, 0.8],
    [center(photo.colorSaturation), pref.saturationPreference, 0.8],
    [center(photo.cropTightness), pref.cropPreference, 0.7],
    [center(1 - photo.backgroundComplexity), pref.backgroundPreference, 0.6],
    // Composition
    [center(photo.ruleOfThirdsScore), pref.compositionAffinity, 0.5],
    [center(photo.symmetryScore), pref.compositionAffinity, 0.4],
  ];

  // Expression signals only contribute if face is present
  if (photo.faceCount > 0) {
    signals.push(
      [center(photo.smileIntensity), pref.smilePreference, 0.6],
      [center(photo.eyeContactScore), pref.eyeContactPreference, 0.5],
      [center(photo.expressionNaturalness), pref.poseNaturalness, 0.4],
    );
  }

  let alignment = 0;
  let totalWeight = 0;
  for (const [photoVal, prefVal, weight] of signals) {
    alignment += photoVal * prefVal * weight;
    totalWeight += weight;
  }

  return totalWeight > 0 ? alignment / totalWeight : 0;
}

// ─── Confidence Ramp ───────────────────────────────────────────

/** Get the maximum PRISM influence allowed at the current confidence level. */
export function getMaxInfluence(confidence: number): number {
  let maxInfluence = 0;
  for (const level of CONFIDENCE_RAMP) {
    if (confidence >= level.minConfidence) {
      maxInfluence = level.maxInfluence;
    }
  }
  return maxInfluence;
}

/** Get the human-readable confidence label. */
export function getConfidenceLabel(confidence: number): string {
  let label = 'Learning';
  for (const level of CONFIDENCE_RAMP) {
    if (confidence >= level.minConfidence) {
      label = level.label;
    }
  }
  return label;
}

// ─── PRISM Score Modifier ──────────────────────────────────────

/**
 * Apply PRISM personalization to a base composite score.
 * Two components: owner boost (personal baseline comparison) + preference boost (style alignment).
 * Always preserves the base score for transparency.
 */
export function applyPrismModifier(
  baseComposite: number,
  dimensionScores: DimensionScores,
  aestheticProps: PrismAestheticProperties,
  ownerBaseline: PrismOwnerBaseline | null,
  preferenceVector: PrismPreferenceVector,
): PrismScoreModifier {
  let ownerBoost = 0;
  let preferenceBoost = 0;
  const reasons: string[] = [];

  // === OWNER-SPECIFIC ADJUSTMENTS ===
  if (aestheticProps.isOwnerPresent && ownerBaseline && ownerBaseline.photoCount >= 10) {
    const portraitScore = dimensionScores.portraitComposition;
    if (portraitScore !== null) {
      const portraitDelta = portraitScore - ownerBaseline.avgPortraitComposition;
      if (portraitDelta > 1.5) {
        ownerBoost += 0.4;
        reasons.push('Portrait quality well above your average');
      } else if (portraitDelta > 0.5) {
        ownerBoost += 0.2;
        reasons.push('Portrait quality above your average');
      }
    }

    // Near personal best → boost
    if (baseComposite >= ownerBaseline.bestCompositeScore * 0.9) {
      ownerBoost += 0.5;
      reasons.push('One of your best photos');
    }
  }

  // === PREFERENCE ALIGNMENT ===
  if (preferenceVector.confidence > 0.1) {
    const alignment = computePreferenceAlignment(aestheticProps, preferenceVector);
    preferenceBoost = alignment * preferenceVector.confidence;

    if (preferenceBoost > 0.3) {
      reasons.push('Matches your preferred style');
    } else if (preferenceBoost < -0.3) {
      reasons.push('Different from your usual style');
      // Don't punish too hard for being different — user might be exploring
      preferenceBoost = Math.max(preferenceBoost, -0.5);
    }
  }

  // === COMBINE & CAP ===
  const maxInfluence = getMaxInfluence(preferenceVector.confidence);
  const totalDelta = clamp(ownerBoost + preferenceBoost, -maxInfluence, maxInfluence);
  const personalizedComposite = clamp(baseComposite + totalDelta, 0, 10);
  // Round to 1 decimal like base scores
  const rounded = Math.round(personalizedComposite * 10) / 10;

  return {
    personalizedComposite: rounded,
    baseComposite,
    prismDelta: rounded - baseComposite,
    adjustmentReasons: reasons,
    ownerBoost,
    preferenceBoost,
    confidence: preferenceVector.confidence,
  };
}

// ─── Owner Baseline ────────────────────────────────────────────

function ema(prev: number, next: number, n: number): number {
  if (n === 0) return next;
  // Adaptive decay: early photos carry more weight, stabilizes toward 0.95
  const alpha = Math.min(0.95, 1 - 1 / (n + 1));
  return alpha * prev + (1 - alpha) * next;
}

/** Update the owner's rolling quality baseline with new photo scores. */
export function updateOwnerBaseline(
  current: PrismOwnerBaseline,
  newScores: DimensionScores,
  compositeScore: number,
): PrismOwnerBaseline {
  const n = current.photoCount;
  return {
    avgPortraitComposition: ema(current.avgPortraitComposition, newScores.portraitComposition ?? 5, n),
    avgLighting: ema(current.avgLighting, newScores.lighting, n),
    avgComposition: ema(current.avgComposition, newScores.composition, n),
    bestPortraitScore: Math.max(current.bestPortraitScore, newScores.portraitComposition ?? 0),
    bestCompositeScore: Math.max(current.bestCompositeScore, compositeScore),
    photoCount: n + 1,
    lastUpdated: new Date().toISOString(),
  };
}

// ─── Owner Heuristic Detection (Web) ───────────────────────────
// VNFeaturePrintRequest is iOS-only. Web uses face-to-frame ratio + position consistency.

export interface OwnerHeuristicInput {
  faceRatio: number;
  nosePosition: { x: number; y: number };
}

/**
 * Heuristic owner detection for web platform.
 * Compares current selfie's face metrics against historical selfie data.
 * Confidence capped at 0.7 (lower than native VNFeaturePrint).
 */
export function detectOwnerHeuristic(
  current: OwnerHeuristicInput,
  history: OwnerHeuristicInput[],
): { isOwner: boolean; confidence: number } {
  if (history.length < 3) return { isOwner: false, confidence: 0 };

  // Check consistency of face ratio and nose position across sessions
  let matchCount = 0;
  for (const prev of history) {
    const ratioClose = Math.abs(current.faceRatio - prev.faceRatio) < 0.10;
    const posClose = Math.sqrt(
      (current.nosePosition.x - prev.nosePosition.x) ** 2 +
      (current.nosePosition.y - prev.nosePosition.y) ** 2,
    ) < 0.15;
    if (ratioClose && posClose) matchCount++;
  }

  const matchRate = matchCount / history.length;
  if (matchRate >= 0.6) {
    return { isOwner: true, confidence: Math.min(0.7, 0.4 + matchRate * 0.3) };
  }
  return { isOwner: false, confidence: 0 };
}

// ─── Behavioral Signal Helpers ─────────────────────────────────

/**
 * Classify photos into positive and negative groups based on behavioral events.
 * Photos with net positive signal weight are "liked", net negative are "disliked".
 */
export function classifyByBehavior(
  events: PrismBehavioralEvent[],
  aestheticsMap: Record<string, PrismAestheticProperties>,
): { positive: PrismAestheticProperties[]; negative: PrismAestheticProperties[] } {
  // Sum signal weights per photo
  const photoWeights = new Map<string, number>();
  for (const event of events) {
    const weight = BEHAVIORAL_SIGNAL_WEIGHTS[event.eventType] ?? 0;
    // For viewed_duration, only count if > 10 seconds
    const effectiveWeight = event.eventType === 'viewed_duration'
      ? (event.value && event.value > 10 ? weight : 0)
      : weight;
    photoWeights.set(event.photoId, (photoWeights.get(event.photoId) ?? 0) + effectiveWeight);
  }

  const positive: PrismAestheticProperties[] = [];
  const negative: PrismAestheticProperties[] = [];

  for (const [photoId, netWeight] of photoWeights) {
    const aesthetics = aestheticsMap[photoId];
    if (!aesthetics) continue;
    if (netWeight > 0.1) positive.push(aesthetics);
    else if (netWeight < -0.1) negative.push(aesthetics);
  }

  return { positive, negative };
}

/**
 * Estimate owner's percentile for a given score based on their baseline.
 * Uses normal approximation around the owner's mean.
 */
export function estimateOwnerPercentile(
  score: number,
  baseline: PrismOwnerBaseline,
): number {
  if (baseline.photoCount < 5) return 50; // Not enough data
  const range = Math.max(1.0, baseline.bestPortraitScore - baseline.avgPortraitComposition);
  const zScore = (score - baseline.avgPortraitComposition) / range;
  return clamp(50 + zScore * 30, 0, 100);
}
